import { useNavigate } from "react-router-dom";
import { OrganizationSwitcher, UserButton, useOrganization } from "@clerk/clerk-react";
import { useState, useEffect } from "react";
import { useApi } from "@/api/useApi";
import { House, CalendarBlank, Image, ChartBar, List, CheckCircle, UsersThree, Gear, PaintBrushBroad, Sparkle, ImageSquare, Palette, Megaphone, Compass, Lifebuoy, VideoCamera, ShieldCheck } from "@phosphor-icons/react";
import { useRole, ROLE_LABELS } from "@/hooks/useRole";
import { usePlatformAdmin } from "@/hooks/usePlatformAdmin";
import { getTenant } from "@/config/tenants";
import { TokenExpiryBanner } from "@/components/TokenExpiryBanner";

const HIDE_CREATE_ORG_APPEARANCE = {
  elements: {
    organizationSwitcherPopoverActionButton__createOrganization: { display: "none" },
    createOrganizationButton: { display: "none" },
  },
};

export default function Layout({ children }) {
  const navigate = useNavigate();
  const api = useApi();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const { role } = useRole();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { organization } = useOrganization();
  const tenant = getTenant();

  useEffect(() => {
    api.get("/version").then((r) => setAppVersion(r.data?.version)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allNavItems = [
    { path: "/", label: "Dashboard", icon: House, testId: "nav-dashboard", roles: ["org:admin", "org:editor"] },
    { path: "/calendar", label: "Calendar", icon: CalendarBlank, testId: "nav-calendar", roles: ["org:admin", "org:editor"] },
    { path: "/creative-director", label: "Creative Director", icon: Compass, testId: "nav-creative-director", roles: ["org:admin", "org:editor"] },
    { path: "/studio", label: "Design Studio", icon: PaintBrushBroad, testId: "nav-studio", roles: ["org:admin", "org:editor"] },
    { path: "/captions", label: "AI Captions", icon: Sparkle, testId: "nav-captions", roles: ["org:admin", "org:editor"] },
    { path: "/images", label: "AI Images", icon: ImageSquare, testId: "nav-images", roles: ["org:admin", "org:editor"] },
    { path: "/ads", label: "AI Ads", icon: Megaphone, testId: "nav-ads", roles: ["org:admin", "org:editor"] },
    { path: "/video", label: "AI Video", icon: VideoCamera, testId: "nav-video", roles: ["org:admin", "org:editor"] },
    { path: "/brand-assets", label: "Brand Assets", icon: Palette, testId: "nav-brand-assets", roles: ["org:admin", "org:editor"] },
    { path: "/approvals", label: "Approvals", icon: CheckCircle, testId: "nav-approvals", roles: ["org:admin", "org:editor"] },
    { path: "/media", label: "Media Library", icon: Image, testId: "nav-media", roles: ["org:admin", "org:editor"] },
    { path: "/analytics", label: "Analytics", icon: ChartBar, testId: "nav-analytics", roles: ["org:admin"] },
    { path: "/team", label: "Team", icon: UsersThree, testId: "nav-team", roles: ["org:admin"] },
    { path: "/settings", label: "Settings", icon: Gear, testId: "nav-settings", roles: ["org:admin"] },
    { path: "/admin", label: "Admin Panel", icon: ShieldCheck, testId: "nav-admin", roles: ["org:admin"] },
    { path: "/help", label: "Help & Getting Started", icon: Lifebuoy, testId: "nav-help", roles: ["org:admin", "org:editor"] },
  ];
  const navItems = allNavItems
    .filter((i) => !role || i.roles.includes(role))
    .filter((i) => i.path !== "/admin" || isPlatformAdmin);

  return (
    <div className="flex h-screen bg-white overflow-hidden">
      <aside className="hidden md:flex md:flex-col w-64 h-full shrink-0 overflow-y-auto bg-brand-cool-grey border-r border-brand-border-grey">
        <div className="p-6 border-b border-brand-border-grey flex items-center gap-3">
          {!tenant.whiteLabel && (
            <img src={tenant.logoUrl || "/logo.png"} alt="" className="w-9 h-9 shrink-0" data-testid="sidebar-logo" />
          )}
          <div>
            <p
              className="text-brand-near-black text-lg font-bold leading-tight"
              style={{ fontFamily: "Montserrat", fontWeight: 700 }}
              data-testid="app-brand-name"
            >
              {tenant.shortName}
            </p>
            <p className="text-brand-deep-teal text-xs font-semibold uppercase tracking-widest mt-1">
              {tenant.tagline}
            </p>
          </div>
        </div>

        {!tenant.whiteLabel && (
        <div className="p-4 border-b border-brand-border-grey">
          <label className="text-brand-muted-grey text-xs uppercase tracking-widest mb-2 block font-semibold">
            Workspace
          </label>
          {organization?.name && (
            <p
              className="text-brand-near-black text-sm font-bold truncate mb-2"
              title={organization.name}
              data-testid="active-workspace-badge"
            >
              {organization.name}
            </p>
          )}
          <div data-testid="workspace-switcher">
            <OrganizationSwitcher
              hidePersonal
              afterSelectOrganizationUrl="/"
              appearance={{ elements: { ...HIDE_CREATE_ORG_APPEARANCE.elements, rootBox: "w-full", organizationSwitcherTrigger: "w-full justify-between" } }}
            />
          </div>
        </div>
        )}

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = window.location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors duration-200 ${
                    isActive
                      ? "bg-brand-teal text-white"
                      : "text-brand-slate hover:bg-brand-pale-teal hover:text-brand-near-black"
                  }`}
                  style={{ borderRadius: 0 }}
                >
                  <Icon size={20} weight="regular" />
                  <span className="text-sm font-semibold">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-brand-border-grey flex items-center gap-3" data-testid="user-menu">
          <UserButton afterSignOutUrl="/app" />
          <span className="text-brand-slate text-xs font-semibold uppercase tracking-wider">
            Account
          </span>
          {appVersion && (
            <span className="ml-auto text-[10px] text-brand-muted-grey font-mono" data-testid="app-version">
              v{appVersion}
            </span>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-brand-cool-grey border-b border-brand-border-grey z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2 min-w-0">
            {!tenant.whiteLabel && (
              <img src={tenant.logoUrl || "/logo.png"} alt="" className="w-6 h-6 shrink-0" data-testid="mobile-logo" />
            )}
            <div className="min-w-0">
              <p className="text-brand-near-black text-sm font-bold leading-tight" style={{ fontFamily: "Montserrat" }} data-testid="app-brand-name-mobile">
                {tenant.shortName}
              </p>
              {!tenant.whiteLabel && organization?.name && (
                <p
                  className="text-brand-deep-teal text-[10px] font-semibold uppercase tracking-wide truncate max-w-[150px]"
                  title={organization.name}
                  data-testid="active-workspace-badge-mobile"
                >
                  {organization.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {navItems.some((i) => i.path === "/help") && (
              <button
                data-testid="mobile-help-icon"
                onClick={() => navigate("/help")}
                className="text-brand-slate hover:text-brand-teal p-2 -m-2"
                aria-label="Help & Getting Started"
              >
                <Lifebuoy size={22} weight="regular" />
              </button>
            )}
            <UserButton afterSignOutUrl="/app" />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-brand-near-black p-2 -m-2"
            >
              <List size={24} weight="regular" />
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="border-t border-brand-border-grey p-4 space-y-2 bg-white overflow-y-auto max-h-[calc(100vh-64px)]" data-testid="mobile-menu-panel">
            {!tenant.whiteLabel && (
            <div className="pb-2" data-testid="workspace-switcher-mobile">
              <OrganizationSwitcher hidePersonal afterSelectOrganizationUrl="/" appearance={HIDE_CREATE_ORG_APPEARANCE} />
            </div>
            )}
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  data-testid={`${item.testId}-mobile`}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-brand-slate hover:bg-brand-pale-teal transition-colors duration-200"
                  style={{ borderRadius: 0 }}
                >
                  <Icon size={20} weight="regular" />
                  <span className="text-sm font-semibold">{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <main className="flex-1 min-w-0 h-full overflow-y-auto p-6 md:p-8 mt-[72px] md:mt-0 bg-white">
        <TokenExpiryBanner />
        {children}
      </main>
    </div>
  );
}
